import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import { useTranslation } from "react-i18next";

import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PrimaryPageNotice } from "@/components/site/PrimaryPageNotice";
import { ContextualLinks } from "@/components/site/ContextualLinks";
import { ScrollProgress } from "@/components/site/ScrollProgress";
import { StickyMobileCTA } from "@/components/site/StickyMobileCTA";
import { ConsentBanner } from "@/components/consent/ConsentBanner";
import { trackPageview } from "@/lib/analytics/track";
import { EventBoundary } from "@/lib/analytics/EventBoundary";
import { GoogleAnalytics } from "@/components/analytics/GoogleAnalytics";
import { MEASUREMENT_ID } from "@/lib/analytics/ga";
import {
  GTM_CONTAINER_ID,
  gtmConsentDefaultScript,
  gtmLoaderScript,
  gtmNoscriptIframeUrl,
} from "@/lib/analytics/gtm";
import { organizationGraph } from "@/lib/seo/schema";
import {
  getPublicBusinessProfile,
  type PublicBusinessProfile,
} from "@/lib/business-profile.functions";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { detectLocale } from "@/i18n/routeMap";
import { hreflangLinks } from "@/i18n/hreflang";

import "@/i18n";

import { NotFound404 } from "@/components/site/NotFound404";

function NotFoundComponent() {
  return <NotFound404 />;
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p role="heading" aria-level={1} className="text-xl font-semibold tracking-tight text-foreground">
          {t("errors.loadFailTitle")}
        </p>

        <p className="mt-2 text-sm text-muted-foreground">{t("errors.loadFailBody")}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-full bg-mint px-5 py-2.5 text-sm font-medium text-mint-foreground"
          >
            {t("errors.retry")}
          </button>
          <a href="/" className="rounded-full border border-white/15 px-5 py-2.5 text-sm">
            {t("errors.home")}
          </a>
        </div>
      </div>
    </div>
  );
}

// Sitewide canonical share image — single source of truth lives i
// `src/lib/seo/constants.ts`. Leaf routes pekar på samma URL så
// LinkedIn/Slack/X/FB alltid plockar samma featured image.
import {
  SOCIAL_IMAGE_URL,
  SOCIAL_IMAGE_ALT,
  SOCIAL_IMAGE_WIDTH,
  SOCIAL_IMAGE_HEIGHT,
} from "@/lib/seo/constants";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // NAP-uppgifterna styrs från Admin → Företagsuppgifter och används i
  // strukturerad data, sidfot och kontaktsida.
  loader: async () => ({ business: await getPublicBusinessProfile() }),
  head: (ctx: {
    matches?: Array<{ pathname?: string }>;
    loaderData?: { business?: PublicBusinessProfile };
  }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0a0a0a" },
      { name: "google-site-verification", content: "4y2xzrQBcFeS2wEtoLbk6GgTZV46UIRKjJQgs57eJ4A" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { property: "og:site_name", content: "Fenny" },
      { property: "og:locale", content: "sv_SE" },
      { property: "og:type", content: "website" },
      { title: "Fenny — Premiumfilm på abonnemang" },
      { property: "og:title", content: "Fenny — Premiumfilm på abonnemang" },
      { name: "twitter:title", content: "Fenny — Premiumfilm på abonnemang" },
      { name: "description", content: "Fenny producerar premiumfilm på abonnemang för varumärken som vill växa med rörligt innehåll i marknadens takt — strategi, produktion och leverans i en tjänst." },
      { property: "og:description", content: "Fenny producerar premiumfilm på abonnemang för varumärken som vill växa med rörligt innehåll i marknadens takt — strategi, produktion och leverans i en tjänst." },
      { name: "twitter:description", content: "Fenny producerar premiumfilm på abonnemang för varumärken som vill växa med rörligt innehåll i marknadens takt — strategi, produktion och leverans i en tjänst." },
      // Canonical featured image — samma URL som Lovable publish settings.
      { property: "og:image", content: SOCIAL_IMAGE_URL },
      { property: "og:image:secure_url", content: SOCIAL_IMAGE_URL },
      { property: "og:image:type", content: "image/webp" },
      { property: "og:image:width", content: SOCIAL_IMAGE_WIDTH },
      { property: "og:image:height", content: SOCIAL_IMAGE_HEIGHT },
      { property: "og:image:alt", content: SOCIAL_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@fenny" },
      { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      { name: "twitter:image:alt", content: SOCIAL_IMAGE_ALT },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Självhostade fonts — preload den primära variabla Onest för snabbare textrendering.
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/onest-variable.woff2", crossOrigin: "anonymous" },
      // YouTube-preconnects flyttade till de routes som faktiskt embeddar (prime, case).

      // Canonical sätts ENDAST per leaf-route. Hreflang sätts ENDAST här,
      // centralt för varje sökväg, så ingen sida kan sakna språkkarta.
      ...hreflangLinks(ctx?.matches?.[ctx.matches.length - 1]?.pathname ?? "/"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          organizationGraph(
            ctx?.loaderData?.business
              ? {
                  phone: ctx.loaderData.business.phoneHref,
                  email: ctx.loaderData.business.email,
                  streetAddress: ctx.loaderData.business.streetAddress,
                  postalCode: ctx.loaderData.business.postalCode,
                  city: ctx.loaderData.business.city,
                  region: ctx.loaderData.business.region,
                  country: ctx.loaderData.business.country,
                  sameAs: ctx.loaderData.business.socials,
                }
              : undefined,
          ),
        ),
      },
      // Google Tag Manager — Consent Mode default måste pushas innan GTM laddas.
      {
        type: "text/javascript",
        children: gtmConsentDefaultScript(),
      },
      // GA4 (gtag.js) laddas med ett EGET dataLayer (gaDataLayer via
      // &l=-parametern). GTM:s gtag-instans äger window.dataLayer och
      // konsumerar inte våra gtag-kommandon, så custom-events (cta_click,
      // generate_lead, conversion …) måste gå genom en isolerad instans
      // för att nå GA4. send_page_view: false — första sidvisningen
      // skickas av GA4-taggen i GTM, SPA-navigeringar skickar appen själv
      // via gaPageview() (se src/lib/analytics/ga.ts).
      ...(MEASUREMENT_ID
        ? [
            {
              type: "text/javascript",
              children: `window.gaDataLayer=window.gaDataLayer||[];window.fennyGa=function(){gaDataLayer.push(arguments)};fennyGa('js',new Date());fennyGa('config','${MEASUREMENT_ID}',{send_page_view:false});`,
            },
            {
              type: "text/javascript",
              src: `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}&l=gaDataLayer`,
              async: true,
            },
          ]
        : []),
      {
        type: "text/javascript",
        children: gtmLoaderScript(GTM_CONTAINER_ID),
      },
      // Plausible Analytics laddas dynamiskt först efter samtycke,
      // se src/lib/analytics/plausible.ts.

    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // FEN-009: <html lang> måste matcha sidans språk redan i SSR-HTML, annars
  // ser Google /en-sidor som svenska. Läs pathname från router-state.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = detectLocale(pathname);
  return (
    <html lang={lang} className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src={gtmNoscriptIframeUrl(GTM_CONTAINER_ID)}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(pathname);
  // Admin- och embed-vyer renderas utan sajtchrome: embeds bäddas in på andra
  // sajter och ska bara visa själva widgeten plus källhänvisningen.
  const isAdminRoute =
    pathname === "/login" ||
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname.startsWith("/embed/");

  useEffect(() => {
    trackPageview();
    const unsub = router.subscribe("onResolved", () => {
      trackPageview();
    });
    return () => unsub();
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider locale={locale}>
        <LazyMotion features={domAnimation} strict={false}>
          <div className="flex min-h-screen flex-col bg-background text-foreground">
            {!isAdminRoute && <ScrollProgress />}
            {!isAdminRoute && <SiteHeader />}
            <main className="flex-1">
              {!isAdminRoute && <PrimaryPageNotice />}
              <Outlet />
            </main>
            {!isAdminRoute && <ContextualLinks />}
            {!isAdminRoute && <SiteFooter />}
            {!isAdminRoute && <StickyMobileCTA />}
            <ConsentBanner />
            <EventBoundary />
            <GoogleAnalytics />
          </div>
        </LazyMotion>
      </LocaleProvider>
    </QueryClientProvider>
  );
}
